import { typographyTokens } from '../../../theme/typographyTokens';
import React from 'react';
import { Box, Typography } from '@mui/material';
import DepartmentIconRenderer, {
  DEPARTMENT_ICON_MAP,
} from '../../../assets/svg/departmentIconMap';
import DefaultDepartmentIcon from '../../../assets/svg/DefaultDepartmentIcon';
import AttachmentsIcon from '../../../assets/svg/AttachmentsIcon';
import CommentIcon from '../../../assets/svg/CommentIcon';
import FileIcon from '../../../assets/svg/FileIcon';
import MyNotesFillIcon from '../../../assets/svg/MyNotesFillIcon';
import MyNotesOutlineIcon from '../../../assets/svg/MyNotesOutlineIcon';
import ReviewCycleApprovedIcon from '../../../assets/svg/ReviewCycleApprovedIcon';
import ReviewCycleAwaitingApprovalIcon from '../../../assets/svg/ReviewCycleAwaitingApprovalIcon';
import ReviewCycleChangeRequestedIcon from '../../../assets/svg/ReviewCycleChangeRequestedIcon';
import TaskListIcon from '../../../assets/svg/TaskListIcon';
import TaskQuestionIcon from '../../../assets/svg/TaskQuestionIcon';
import TaskReviewCheckIcon from '../../../assets/svg/TaskReviewCheckIcon';
import ArchitectureIcon from '../../../assets/svg/ArchitectureIcon';
import SolutionArchitectConsultantIcon from '../../../assets/svg/SolutionArchitectConsultantIcon';
import IdeationIcon from '../../../assets/svg/LightBulbIcon'; 
import DefaultWorkflowIcon from '../../../assets/svg/DefaultWorkflowIcon';
import AIInviteOnlyIcon from '../../../assets/svg/AIInviteOnlyIcon';
import OnTheClockIcon from '../../../assets/svg/OnTheClockIcon';
import FavoriteIcon from '../../../assets/svg/FavoriteIcon';
import PinItemIcon from '../../../assets/svg/PinItemIcon';
import ProjectFolderIcon from '../../../assets/svg/ProjectFolderIcon';
import UserIcon from '../../../assets/svg/UserIcon';

const iconTileSx = {
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  padding: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  backgroundColor: '#fafbfd',
};

const standaloneIcons = [
  { name: 'task-list', preview: <TaskListIcon height={20} width={20} /> },
  { name: 'attachment', preview: <AttachmentsIcon height={20} width={20} /> },
  { name: 'comment', preview: <CommentIcon height={20} width={20} /> },
  { name: 'my-notes-outline', preview: <MyNotesOutlineIcon height={20} width={20} /> },
  { name: 'my-notes-fill', preview: <MyNotesFillIcon height={20} width={20} /> },
  { name: 'task-question', preview: <TaskQuestionIcon height={20} width={20} /> },
  { name: 'file', preview: <FileIcon height={20} width={20} /> },
  {
    name: 'review-cycle-awaiting-approval',
    preview: <ReviewCycleAwaitingApprovalIcon height={20} width={20} />,
  },
  {
    name: 'review-cycle-change-requested',
    preview: <ReviewCycleChangeRequestedIcon height={20} width={20} />,
  },
  { name: 'review-cycle-approved', preview: <ReviewCycleApprovedIcon height={20} width={20} /> },
  { name: 'pin-item', preview: <PinItemIcon height={20} width={20} /> },
  { name: 'user', preview: <UserIcon height={20} width={20} /> },
  { name: 'task-review-check', preview: <TaskReviewCheckIcon height={20} width={20} /> },
  { name: 'architecture', preview: <ArchitectureIcon height={20} width={20} /> },
  { name: 'on-the-clock', preview: <OnTheClockIcon height={20} width={20} /> },
  { name: 'favorite', preview: <FavoriteIcon height={20} width={20} /> },
  { name: 'project-folder', preview: <ProjectFolderIcon height={20} width={20} /> },
];

const workflowIcons = [
  { name: 'SEP Design Review', preview: <ArchitectureIcon height={20} width={20} /> },
  {
    name: 'Solution Architect Consultation',
    preview: <SolutionArchitectConsultantIcon height={20} width={20} />,
  },
  { name: 'Ideation', preview: <IdeationIcon height={20} width={20} /> },
  { name: 'Default Workflow', preview: <DefaultWorkflowIcon height={20} width={20} /> },
  { name: 'AI Invite Only', preview: <AIInviteOnlyIcon height={20} width={20} /> },
];

const IconGallery = () => {
  const departmentIconNames = Object.keys(DEPARTMENT_ICON_MAP).sort();
  const departmentIcons = [
    { name: 'Default', preview: <DefaultDepartmentIcon height={20} width={20} /> },
    ...departmentIconNames.map((iconName) => ({
      name: iconName,
      preview: <DepartmentIconRenderer departmentName={iconName} height={20} width={20} />,
    })),
  ];

  return (
    <Box>
      <Typography sx={{ fontSize: typographyTokens.fontSizes.sm, fontWeight: typographyTokens.fontWeights.bold, mb: 1.5 }}>
        Department Icons
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 1.5,
          mb: 3,
        }}
      >
        {departmentIcons.map((icon) => (
          <Box key={icon.name} sx={iconTileSx}>
            {icon.preview}
            <Typography sx={{ fontSize: typographyTokens.fontSizes.caption }}>{icon.name}</Typography>
          </Box>
        ))}
      </Box>

      <Typography sx={{ fontSize: typographyTokens.fontSizes.sm, fontWeight: typographyTokens.fontWeights.bold, mb: 1.5 }}>
        Workflow Icons
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 1.5,
          mb: 3,
        }}
      >
        {workflowIcons.map((icon) => (
          <Box key={icon.name} sx={iconTileSx}>
            {icon.preview}
            <Typography sx={{ fontSize: typographyTokens.fontSizes.caption }}>{icon.name}</Typography>
          </Box>
        ))}
      </Box>

      <Typography sx={{ fontSize: typographyTokens.fontSizes.sm, fontWeight: typographyTokens.fontWeights.bold, mb: 1.5 }}>
        Standalone Icons
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 1.5,
        }}
      >
        {standaloneIcons.map((icon) => (
          <Box key={icon.name} sx={iconTileSx}>
            {icon.preview}
            <Typography sx={{ fontSize: typographyTokens.fontSizes.caption }}>{icon.name}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default IconGallery;
